import React, { useEffect, useState } from 'react';
import { updateOrganization } from '../../../../repository/OrganizationDAO';
import { useLoadingContext } from '../../../../context/loading/LoadingContext';
import { TextField, Button, Grid, MenuItem } from '@mui/material';
import BaseModal from '../../../../components/modals/BaseModal';
import { MuiTelInput } from 'mui-tel-input';
import { useToast, ToastType } from '../../../../context/toast/ToastContext';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useGooglePlacesAutocomplete } from '../../../../components/address_autocomplete/useGooglePlacesAutoComplete';
import { USAbbreviations } from '../../../../utils/select_helpers/UnitedStatesAbbreviations';
import { validateZipCode } from '../../../../utils/validation/ZipCodeValidation';
import CustomAddressAutocomplete from '../../../../components/address_autocomplete/CustomAddressAutocomplete';

const EditOrganizationModal = ({ show, onHide, organization }) => {
    const { address, city, state, zipCode, addressPredictions, handleAddressInputChange, handleAddressSelect, setAddress, setCity, setState, setZipCode } = useGooglePlacesAutocomplete(show);
    const { setLoadingData } = useLoadingContext();
    const [organizationName, setOrganizationName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const { showToast } = useToast();
    const { t } = useTranslation();
    const [zipCodeError, setZipCodeError] = useState(false);

    useEffect(() => {
        if (organization) {
            setOrganizationName(organization.name);
            setPhoneNumber(organization.phone_number);
            setAddress(organization.address);
            setCity(organization.city);
            setState(organization.state);
            setZipCode(organization.zip);
        }
    }, [organization, show]);

    const handleZipCodeChange = (e) => {
        const newZip = e.target.value;
        setZipCodeError(!validateZipCode(newZip));
        setZipCode(newZip);
    };

    const handleSubmit = async () => {
        let valid = true;    
        if (!organizationName.trim()) valid = false;
        if (!phoneNumber.trim()) valid = false;
        if (!address.trim()) valid = false;
        if (!city.trim()) valid = false;
        if (!state.trim()) valid = false;
        if (!validateZipCode(zipCode)) {
            setZipCodeError(true);
            valid = false;
        }
    
        if (!valid) return;
    
        setLoadingData(true);
        const updatedOrganization = {
            name: organizationName,
            phone_number: phoneNumber,
            address: address,
            city: city,
            state: state,
            zip: zipCode,
        };
    
        try {
            const result = await updateOrganization(organization.id, updatedOrganization);
    
            if (result.success) {
                showToast(t(StringKeys.SUCCESSFULLY_UPDATED_X, {x: `${organizationName}`}), ToastType.SUCCESS);
                onHide();
            } else {
                showToast(t(StringKeys.FAILED_TO_UPDATE_X, {x:`${organizationName}`}), ToastType.DANGER);
            }
        } catch (error) {
            showToast(t(StringKeys.FAILED_TO_UPDATE_X, {x:`${organizationName}`}), ToastType.DANGER);
        } finally {
            setLoadingData(false);
        }
    };
    
    const resetForm = () => {
        setOrganizationName('');
        setPhoneNumber('');
        setAddress('')
        setCity('')
        setState('')
        setZipCode('')
        setZipCodeError(false);
    };

    const actions = (
        <Button onClick={handleSubmit}>{t(StringKeys.SUBMIT)}</Button>
    );

    const handlePhoneNumberChange = (newValue) => {
        setPhoneNumber(newValue);
    };

    return (
        <BaseModal
            open={show}
            onClose={() => {
                resetForm();
                onHide();
            }}
            title={t(StringKeys.EDIT_X, {x:`${organization?.name}`})}
            actions={actions}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        margin="dense"
                        id="organization-name"
                        label={t(StringKeys.COMPANY_NAME)}
                        type="text"
                        fullWidth
                        value={organizationName}
                        onChange={(e) => setOrganizationName(e.target.value)}
                        variant="outlined"
                    />
                </Grid>
                <Grid item xs={12}>
                    <MuiTelInput
                        label={t(StringKeys.PHONE)}
                        defaultCountry='US'
                        fullWidth
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CustomAddressAutocomplete
                        value={address}
                        onChange={handleAddressInputChange}
                        onSelect={(selectedOption) => {
                            if (selectedOption) {
                                handleAddressSelect(null, selectedOption);
                            }
                        }}
                        options={addressPredictions}
                        label={t(StringKeys.ADDRESS)}
                        fullWidth={true}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        margin="dense"
                        id="city"
                        label={t(StringKeys.CITY)}
                        type="text"
                        fullWidth
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        variant="outlined"
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        select
                        margin="dense"
                        id="state"
                        label={t(StringKeys.STATE)}
                        type="text"
                        fullWidth
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        variant="outlined"
                    >
                        {USAbbreviations.map((abbreviation) => (
                            <MenuItem key={abbreviation} value={abbreviation}>
                                {abbreviation}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        margin="dense"
                        id="zip"
                        label={t(StringKeys.ZIP_CODE)}
                        type="text"
                        fullWidth
                        value={zipCode}
                        onChange={handleZipCodeChange}
                        error={zipCodeError}
                        helperText={zipCodeError ? t(StringKeys.PLEASE_ENTER_A_VALID_ZIP_CODE) : ""}
                        variant="outlined"
                    />
                </Grid>
            </Grid>
        </BaseModal>
    );
};

export default EditOrganizationModal;